import axios from "axios";
import apiRoutes from "./apiRoutes";

// === Main API Client Creation ===
let failedQueue = [];
let isRefreshing = false;

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(),
  );
  failedQueue = [];
};

export function apiClientCreate({
  options,
  getCurrentAccessToken,
  getCurrentRefreshToken,
  refreshTokenUrl,
  logout,
  setRefreshedTokens,
}) {
  const apiClient = axios.create(options);

  // === Request Interceptor: Inject Authorization Header ===
  apiClient.interceptors.request.use(
    (config) => {
      if (config.authorization !== false) {
        const token = getCurrentAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      // Add Accept-Language header for i18n support
      const currentLanguage = localStorage.getItem("i18nextLng") || "en";
      config.headers["Accept-Language"] = currentLanguage;
      return config;
    },
    (error) => Promise.reject(error),
  );

  // === Response Interceptor: Handle Token Expiry and Refresh ===
  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      const originalRequest = error.config;
      originalRequest.headers = JSON.parse(
        JSON.stringify(originalRequest.headers || {}),
      );
      const refreshToken = getCurrentRefreshToken();

      const handleError = (error) => {
        processQueue(error);
        logout();
        return Promise.reject(error);
      };

      if (
        refreshToken &&
        error.response?.status === 401 &&
        error.response?.data?.code === "token_not_valid" &&
        originalRequest?.url !== refreshTokenUrl &&
        originalRequest?._retry !== true
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => apiClient(originalRequest));
        }

        isRefreshing = true;
        originalRequest._retry = true;

        return apiClient
          .post(refreshTokenUrl, { refresh: refreshToken })
          .then((res) => {
            const tokens = {
              access: res.data?.access,
              refresh: res.data?.refresh,
            };
            setRefreshedTokens(tokens);
            processQueue(null);
            return apiClient(originalRequest);
          }, handleError)
          .finally(() => {
            isRefreshing = false;
          });
      }

      if (
        originalRequest.url === apiRoutes.token_obtain_pair ||
        (error.response?.status === 401 &&
          error.response?.data?.code === "token_not_valid")
      ) {
        return handleError(error);
      }

      return Promise.reject(error);
    },
  );

  return apiClient;
}
